import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { isEmployeeRole } from "@/lib/constants";
import { osloDateString } from "@/lib/oslo-time";
import {
  addDaysToDateString,
  getLastNWorkdays,
  findMissingWorkdays,
  type AbsenceCover,
} from "@/lib/missing-hours";

/**
 * Antall arbeidsdager bakover vi ser etter hull i. Samme tall som det daglige
 * cron-varselet (VIB-1296) bruker, så popupen og e-posten aldri er uenige om
 * hva som mangler.
 */
const WORKDAYS_TO_CHECK = 2;

/**
 * Hvor langt tilbake vi henter data for å finne de arbeidsdagene. Romslig nok
 * til å dekke en juleferie eller påskeuke uten å finne færre enn to.
 */
const LOOKBACK_DAYS = 30;

export interface MissingHoursResult {
  /** Arbeidsdager uten en eneste timeføring, «YYYY-MM-DD», eldst først. */
  missingDays: string[];
}

/**
 * VIB-2093: hvilke av de siste arbeidsdagene den aktive brukeren mangler
 * timeføring for.
 *
 * Bruker `useImpersonation()`, ikke `useAuth()`: impersonerer en superadmin en
 * ansatt, skal flaten vise den ansattes hull, ikke superadminens egne.
 *
 * Kun for ansatte (`isEmployeeRole`). En revisor med `external`-rolle fører
 * ikke timer og skal aldri få påminnelsen. Dette er strengere enn cron-en, som
 * ser på alle aktive brukere.
 */
export function useMissingHours(): {
  data: MissingHoursResult | undefined;
  isLoading: boolean;
} {
  const { activeUser } = useImpersonation();

  const userId = activeUser?.id ?? null;
  const enabled = !!userId && isEmployeeRole(activeUser?.role);

  // Datovinduet er avledet av dagens dato, ikke av et Date-objekt, slik at
  // queryKey-en er stabil gjennom dagen og ikke trigger refetch ved hver render.
  const today = osloDateString(new Date());
  const from = addDaysToDateString(today, -LOOKBACK_DAYS);

  const query = useQuery({
    queryKey: ["missing-hours", userId, today],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<MissingHoursResult> => {
      // Alle tre er bundet til én bruker og et 30-dagers vindu, så ingen av
      // dem kan treffe Supabase sin stille 1000-rads-grense.
      const [holidayRes, entryRes, absenceRes] = await Promise.all([
        supabase
          .from("company_holidays")
          .select("date")
          .gte("date", from)
          .lte("date", today),
        supabase
          .from("time_entries")
          .select("start_date")
          .eq("user_id", userId!)
          .gte("start_date", from)
          .lte("start_date", today),
        supabase
          .from("absences")
          .select("status, start_date, end_date, hours")
          .eq("user_id", userId!)
          .lte("start_date", today)
          .gte("end_date", from),
      ]);

      if (holidayRes.error) throw holidayRes.error;
      if (entryRes.error) throw entryRes.error;
      if (absenceRes.error) throw absenceRes.error;

      const holidays = new Set(
        (holidayRes.data ?? []).map((r: { date: string }) => r.date),
      );
      // start_date er timestamptz («2026-10-23T07:00:00+00:00»). Settet må
      // holde Oslo-datoen, ellers treffer ingen føring arbeidsdagen og alle
      // får varsel hver dag. Samme konvertering som cron-varselet.
      const datesWithEntries = new Set(
        (entryRes.data ?? []).map((r: { start_date: string }) => osloDateString(r.start_date)),
      );
      const absences = (absenceRes.data ?? []) as AbsenceCover[];

      const workdays = getLastNWorkdays(new Date(), WORKDAYS_TO_CHECK, holidays);
      return { missingDays: findMissingWorkdays(workdays, datesWithEntries, absences) };
    },
  });

  return { data: query.data, isLoading: query.isLoading };
}
